// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { CompanyService, ToastService } from '@lfx/core/services';
import {
  KeyProject,
  Workspace,
  ProjectDetailResponse,
  InfluenceApiResponse,
} from '@lfx/key-projects/key-projects.model';
import { TimeRangeValue } from '@lfx/key-projects/entities';
import { MembershipDetails } from '@lfx/key-projects/components/our-membership/our-membership.component';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { KeyProjectsStateService } from './state/key-projects-state.service';
import { WorkspaceApiService } from './api/workspace-api.service';
import { ProjectApiService } from './api/project-api.service';
import { MembershipService } from './api/membership.service';
import { InfluenceDataService } from './api/influence-data.service';
import { MetricChartDataService } from './api/metric-chart-data.service';
import { DefaultWorkspaceValidationService } from './validation/default-workspace-validation.service';
import { API_CONSTANTS } from '@lfx/key-projects/key-projects.constants';

/**
 * Facade service for Key Projects feature
 * Coordinates between state management and API services
 * Delegates to specialized services following Single Responsibility Principle
 */
@Injectable({
  providedIn: 'root',
})
export class KeyProjectsService {
  private readonly companyService = inject(CompanyService);
  private readonly toastService = inject(ToastService);
  private readonly stateService = inject(KeyProjectsStateService);
  private readonly workspaceApi = inject(WorkspaceApiService);
  private readonly projectApi = inject(ProjectApiService);
  private readonly membershipService = inject(MembershipService);
  private readonly influenceService = inject(InfluenceDataService);
  private readonly metricChartService = inject(MetricChartDataService);
  private readonly workspaceValidation = inject(DefaultWorkspaceValidationService);

  // Expose state read-only accessors
  readonly projects = this.stateService.projects;
  readonly workspaces = this.stateService.workspaces;
  readonly viewState = this.stateService.viewState;
  readonly loading = this.stateService.loading;
  readonly initialLoading = this.stateService.initialLoading;
  readonly initialError = this.stateService.initialError;
  readonly creatingDefaultWorkspaces = this.stateService.creatingDefaultWorkspaces;
  readonly loadingWorkspaceProjects = this.stateService.loadingWorkspaceProjects;
  readonly lastProjectDetails = this.projectApi.lastProjectDetails;

  // Expose computed properties
  readonly currentProjects = this.stateService.currentProjects;
  readonly emptyStateConfig = this.stateService.emptyStateConfig;

  constructor() {
    this.loadInitialData();
  }

  // ===============================
  // INITIALIZATION
  // ===============================

  /**
   * Load initial data from backend
   * Note: loadWorkspaces() already handles default workspace creation when length === 0
   * So we just need to wait for it to complete and set loading state
   */
  private loadInitialData(): void {
    this.stateService.setInitialLoading(true);
    this.stateService.setInitialError(null);

    this.loadWorkspaces().subscribe({
      next: () => {
        // loadWorkspaces() already handles workspace creation when needed
        // Just set loading to false since workspace loading/creation is complete
        this.stateService.setInitialLoading(false);
      },
      error: () => {
        this.stateService.setInitialError('Failed to load workspaces. Please try again.');
        this.stateService.setInitialLoading(false);
      },
    });
  }

  /**
   * Retry initial data loading
   */
  retryInitialLoad(): void {
    this.loadInitialData();
  }

  // ===============================
  // STATE QUERIES
  // ===============================

  getProjectById(id: string): KeyProject | undefined {
    return this.stateService.getProjectById(id);
  }

  getWorkspaceById(id: string): Workspace | undefined {
    return this.stateService.getWorkspaceById(id);
  }

  getProjectsForWorkspace(workspaceId: string): KeyProject[] {
    return this.stateService.getProjectsForWorkspace(workspaceId);
  }

  // ===============================
  // WORKSPACE OPERATIONS
  // ===============================

  /**
   * Load all workspaces from backend
   * Automatically triggers default workspace creation based on validation rules
   */
  loadWorkspaces(): Observable<Workspace[]> {
    this.stateService.setLoading(true);

    return this.workspaceApi.loadWorkspaces().pipe(
      switchMap(workspaces => {
        this.stateService.setWorkspaces(workspaces);
        this.stateService.setLoading(false);

        // Check if default workspaces should be created
        const validationResult = this.workspaceValidation.shouldCreateDefaultWorkspaces(workspaces);
        if (validationResult.shouldCreateDefaults) {
          this.stateService.setCreatingDefaultWorkspaces(true);
          return this.createDefaultWorkspaces().pipe(
            map(() => workspaces),
            catchError(() => {
              this.stateService.setCreatingDefaultWorkspaces(false);
              return of(workspaces);
            })
          );
        }

        this.handleWorkspaceAutoSelection(workspaces);
        return of(workspaces);
      }),
      catchError(() => {
        this.stateService.setLoading(false);
        return of([]);
      })
    );
  }

  /**
   * Handle auto-selection of first workspace
   */
  private handleWorkspaceAutoSelection(workspaces: Workspace[]): void {
    const currentViewState = this.viewState();
    if (workspaces.length > 0 && !currentViewState.selectedWorkspaceId) {
      const firstWorkspace = workspaces[0];
      this.stateService.selectWorkspace(firstWorkspace.id);

      if (firstWorkspace.projectSlugs && firstWorkspace.projectSlugs.length > 0) {
        this.loadWorkspaceProjects(firstWorkspace.id).subscribe();
      }
    }
  }

  /**
   * Create a new workspace
   */
  createWorkspace(name: string): Observable<Workspace> {
    return this.workspaceApi.createWorkspace(name).pipe(
      tap(newWorkspace => {
        this.stateService.addWorkspace(newWorkspace);
        this.stateService.selectWorkspace(newWorkspace.id);
      })
    );
  }

  /**
   * Rename an existing workspace
   */
  renameWorkspace(id: string, name: string): Observable<Workspace> {
    const originalWorkspaces = this.workspaces();

    // Optimistic update
    this.stateService.updateWorkspaces(workspaces =>
      workspaces.map(ws => (ws.id === id ? { ...ws, name: name.trim() } : ws))
    );

    return this.workspaceApi.renameWorkspace(id, name).pipe(
      tap(workspace => {
        this.stateService.updateWorkspaces(workspaces =>
          workspaces.map(ws => (ws.id === id ? { ...ws, ...workspace } : ws))
        );
      }),
      catchError(err => {
        // Rollback on error
        this.stateService.setWorkspaces(originalWorkspaces);
        throw err;
      })
    );
  }

  /**
   * Delete a workspace
   */
  deleteWorkspace(id: string, forceRemoveProjects = false): Observable<void> {
    const originalWorkspaces = this.workspaces();
    const currentViewState = this.viewState();
    const workspace = this.getWorkspaceById(id);

    // Optimistic update
    this.stateService.removeWorkspace(id);

    if (currentViewState.mode === 'workspace-projects' && currentViewState.selectedWorkspaceId === id) {
      this.stateService.selectFirstWorkspace();
    }

    const deleteOperation = forceRemoveProjects && workspace?.projectSlugs
      ? this.workspaceApi.removeAllProjectsFromWorkspace(id, workspace.projectSlugs).pipe(
          switchMap(() => this.workspaceApi.deleteWorkspace(id))
        )
      : this.workspaceApi.deleteWorkspace(id);

    return deleteOperation.pipe(
      catchError(err => {
        // Rollback on error
        this.stateService.setWorkspaces(originalWorkspaces);
        this.stateService.setViewState(currentViewState);
        throw err;
      })
    );
  }

  /**
   * Navigate to projects within a workspace
   */
  showWorkspaceProjects(workspaceId: string): void {
    this.stateService.selectWorkspace(workspaceId);
    this.loadWorkspaceProjects(workspaceId).subscribe();
  }

  /**
   * Refresh all data
   */
  refreshData(): Observable<{ workspaces: Workspace[]; projects: KeyProject[] }> {
    const selectedWorkspaceId = this.viewState().selectedWorkspaceId;

    return this.loadWorkspaces().pipe(
      switchMap(workspaces => {
        if (selectedWorkspaceId) {
          const workspace = workspaces.find(ws => ws.id === selectedWorkspaceId);
          if (workspace?.projectSlugs && workspace.projectSlugs.length > 0) {
            return forkJoin({
              workspaces: of(workspaces),
              projects: this.loadWorkspaceProjects(selectedWorkspaceId),
            });
          }
        }
        return of({ workspaces, projects: [] });
      })
    );
  }

  /**
   * Refresh only workspaces data
   */
  refreshWorkspaces(): Observable<Workspace[]> {
    return this.loadWorkspaces();
  }

  /**
   * Rebuild all workspaces (DEV/LOCAL only)
   * Deletes all existing workspaces and recreates the 3 default workspaces
   */
  rebuildAllWorkspaces(): Observable<void> {
    this.stateService.setLoading(true);
    this.stateService.setLoadingWorkspaceProjects(true);

    return this.workspaceApi.rebuildAllWorkspaces().pipe(
      switchMap(result => {
        // Reload workspaces after rebuild
        return this.workspaceApi.loadWorkspaces().pipe(
          map(workspaces => ({ result, workspaces }))
        );
      }),
      switchMap(({ result, workspaces }) => {
        this.stateService.setWorkspaces(workspaces);

        // Select first workspace if available
        if (workspaces.length > 0) {
          const firstWorkspace = workspaces[0];
          this.stateService.selectWorkspace(firstWorkspace.id);

          if (firstWorkspace.projectSlugs && firstWorkspace.projectSlugs.length > 0) {
            return this.loadWorkspaceProjects(firstWorkspace.id).pipe(
              map(() => result)
            );
          }
        }

        return of(result);
      }),
      tap(result => {
        this.stateService.setLoading(false);
        this.stateService.setLoadingWorkspaceProjects(false);
        this.toastService.showSuccess(result.message);
      }),
      map(() => void 0),
      catchError(err => {
        this.stateService.setLoading(false);
        this.stateService.setLoadingWorkspaceProjects(false);
        this.toastService.showError(err.message || 'Failed to rebuild workspaces');
        throw err;
      })
    );
  }

  // ===============================
  // PROJECT OPERATIONS
  // ===============================

  /**
   * Load projects for a specific workspace
   * @param workspaceId The workspace ID to load projects for
   * @param throwOnError If true, propagate errors instead of returning empty array
   */
  loadWorkspaceProjects(workspaceId: string, throwOnError = false): Observable<KeyProject[]> {
    this.stateService.setLoadingWorkspaceProjects(true);

    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          return of([]);
        }

        const workspace = this.getWorkspaceById(workspaceId);
        if (!workspace?.projectSlugs || workspace.projectSlugs.length === 0) {
          return of([]);
        }

        return this.projectApi.loadProjectsByIds(company.salesforceB2BAccountId, workspace.projectSlugs, throwOnError);
      }),
      tap(projects => {
        this.stateService.updateProjects(allProjects => {
          const existingIds = new Set(allProjects.map(p => p.id));
          const newProjects = projects.filter(p => !existingIds.has(p.id));
          const updatedProjects = [...allProjects, ...newProjects];

          const projectMap = new Map(projects.map(p => [p.id, p]));
          return updatedProjects.map(p => (projectMap.has(p.id) ? projectMap.get(p.id)! : p));
        });
        this.stateService.setLoadingWorkspaceProjects(false);
      }),
      catchError((err) => {
        this.stateService.setLoadingWorkspaceProjects(false);
        if (throwOnError) {
          return throwError(() => err);
        }
        return of([]);
      })
    );
  }

  /**
   * Add projects to a workspace
   */
  addProjectsToWorkspace(projectSlugs: string[]): Observable<void> {
    const workspaceId = this.viewState().selectedWorkspaceId;
    if (!workspaceId) {
      return of(void 0);
    }

    return this.workspaceApi.addProjectsToWorkspace(workspaceId, projectSlugs).pipe(
      tap(() => {
        this.stateService.updateWorkspaces(workspaces =>
          workspaces.map(ws =>
            ws.id === workspaceId
              ? { ...ws, projectSlugs: [...(ws.projectSlugs || []), ...projectSlugs] }
              : ws
          )
        );

        const updatedWorkspace = this.getWorkspaceById(workspaceId);
        if (updatedWorkspace) {
          const currentState = this.viewState();
          this.stateService.setViewState({
            ...currentState,
            selectedWorkspace: updatedWorkspace,
          });
        }
      }),
      switchMap(() => this.loadWorkspaceProjects(workspaceId)),
      map(() => void 0)
    );
  }

  /**
   * Remove a project from a workspace
   */
  removeProjectFromWorkspace(workspaceId: string, projectId: string): Observable<void> {
    const project = this.getProjectById(projectId);
    if (!project) {
      return of(void 0);
    }

    const originalWorkspaces = this.workspaces();
    const expectedFormat = `${project.id}:${project.slug}`;

    // Optimistic update
    this.stateService.updateWorkspaces(workspaces =>
      workspaces.map(ws => {
        if (ws.id === workspaceId) {
          const newSlugs = (ws.projectSlugs || []).filter(
            slug => slug !== expectedFormat && slug !== project.slug
          );
          return { ...ws, projectSlugs: newSlugs };
        }
        return ws;
      })
    );

    const updatedWorkspace = this.getWorkspaceById(workspaceId);
    if (updatedWorkspace) {
      const currentState = this.viewState();
      this.stateService.setViewState({
        ...currentState,
        selectedWorkspace: updatedWorkspace,
      });
    }

    return this.workspaceApi.removeProjectFromWorkspace(workspaceId, expectedFormat).pipe(
      catchError(err => {
        // Rollback on error
        this.stateService.setWorkspaces(originalWorkspaces);
        const originalWorkspace = originalWorkspaces.find(ws => ws.id === workspaceId);
        if (originalWorkspace) {
          const currentState = this.viewState();
          this.stateService.setViewState({
            ...currentState,
            selectedWorkspace: originalWorkspace,
          });
        }
        throw err;
      })
    );
  }

  /**
   * Search projects for typeahead functionality
   */
  searchProjects(searchTerm: string): Observable<
    {
      id: string;
      name: string;
      logo?: string;
      slug?: string;
      description?: string;
    }[]
  > {
    return this.projectApi.searchProjects(searchTerm);
  }

  /**
   * Get project details
   */
  getProjectDetails(slug: string): Observable<ProjectDetailResponse> {
    return this.projectApi.getProjectDetails(slug);
  }

  // ===============================
  // DEFAULT WORKSPACE CREATION
  // ===============================

  /**
   * Create default workspaces with auto-populated projects based on activity
   * Creates 3 workspaces: Most Active, All Projects, Key Projects
   */
  private createDefaultWorkspaces(): Observable<void> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        return this.workspaceApi.createDefaultWorkspaces(
          company.salesforceB2BAccountId,
          API_CONSTANTS.DEFAULT_WORKSPACE_LIMIT
        );
      }),
      switchMap(response => {
        // Extract successful workspaces
        const successfulWorkspaces = response.workspaces
          .filter(w => w.success && w.workspace)
          .map(w => w.workspace!);

        if (successfulWorkspaces.length === 0) {
          this.toastService.showInfo('No active projects found to create workspaces');
          return of(void 0);
        }

        // Add all successful workspaces at once
        this.stateService.addWorkspaces(successfulWorkspaces);

        // Select "Most Active Projects" workspace by default
        const mostActiveWorkspace = successfulWorkspaces.find(
          ws => ws.name === 'Most Active Projects'
        );

        const workspaceToSelect = mostActiveWorkspace || successfulWorkspaces[0];
        this.stateService.selectWorkspace(workspaceToSelect.id);

        // Load projects for the selected workspace if it has any
        if (workspaceToSelect.projectSlugs && workspaceToSelect.projectSlugs.length > 0) {
          return this.loadWorkspaceProjects(workspaceToSelect.id, true).pipe(
            map(() => void 0),
            catchError(() => {
              this.toastService.showWarning(
                'Workspaces created successfully, but failed to load some projects. Please try refreshing.'
              );
              return of(void 0);
            })
          );
        }

        return of(void 0);
      }),
      tap(() => {
        this.stateService.setCreatingDefaultWorkspaces(false);
      }),
      catchError(err => {
        this.stateService.setCreatingDefaultWorkspaces(false);
        if (err.message?.includes('No active projects found')) {
          this.toastService.showInfo(err.message);
        } else {
          this.toastService.showError('Failed to create default workspaces. Please try again.');
        }
        throw err;
      })
    );
  }

  // ===============================
  // HELPER DELEGATIONS
  // ===============================

  getHealthScoreDisplayText(level: string): string {
    return KeyProjectsHelper.getHealthScoreDisplayText(level);
  }

  transformToHealthMetrics(
    healthScoreData: unknown
  ): { label: string; percent: number; value: number; bgColor: string }[] {
    return KeyProjectsHelper.transformToHealthMetrics(healthScoreData);
  }

  // ===============================
  // MEMBERSHIP
  // ===============================

  getMembershipDetails(projectSlug: string): Observable<MembershipDetails | null> {
    return this.membershipService.getMembershipDetails(projectSlug);
  }

  // ===============================
  // INFLUENCE DATA
  // ===============================

  getEcosystemInfluenceData(
    projectSlug: string,
    timeRangeType: TimeRangeValue = 'past_2_years',
    metricType?: string
  ): Observable<InfluenceApiResponse> {
    return this.influenceService.getEcosystemInfluenceData(projectSlug, timeRangeType, metricType);
  }

  getTechnicalInfluenceData(projectSlug: string, timeRangeType: TimeRangeValue = 'past_2_years'): Observable<InfluenceApiResponse> {
    return this.influenceService.getTechnicalInfluenceData(projectSlug, timeRangeType);
  }

  // ===============================
  // METRIC CHART DATA
  // ===============================

  getMetricChartColumns(metricType?: string): Observable<Column[]> {
    return this.metricChartService.getMetricChartColumns(metricType);
  }

  getMetricChartData(metricType?: string, projectSlug?: string): Observable<DataRow[]> {
    return this.metricChartService.getMetricChartData(metricType, projectSlug);
  }

  getSummaryTableData(metricType?: string, projectSlug?: string): Observable<{ columns: Column[]; data: DataRow[] }> {
    return this.metricChartService.getSummaryTableData(metricType, projectSlug);
  }
}
